import React, { useState, useEffect } from 'react';
import { BottomSheet, ButtonList } from '../../../shared/ui';
import { sitesV2Api, SiteMember } from '../../../api/sitesV2Api';
import { questionsV2Api, QuestionMember } from '../../../api/questionsV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { RoleBadges } from '../../../shared/ui/RoleBadges';
import { ProfilePage } from '../../../features/profile';
import config from '../../../config/config';
import ChatMemberPermissionsModal from './ChatMemberPermissionsModal';
import './QuestionMembersModal.css';

interface QuestionMembersModalProps {
  isOpen: boolean;
  siteId: number;
  questionId: number;
  typeId?: number; // 1=Question, 2=Note, 3=Supply
  members: QuestionMember[];
  onClose: () => void;
  onMembersUpdated: () => void;
}

const QuestionMembersModal: React.FC<QuestionMembersModalProps> = ({
  isOpen,
  siteId,
  questionId,
  typeId = 1,
  members,
  onClose,
  onMembersUpdated
}) => {
  const { user } = useAuth();
  const [siteMembers, setSiteMembers] = useState<SiteMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addingMember, setAddingMember] = useState<number | null>(null);
  const [permissionsMember, setPermissionsMember] = useState<{ userId: number; userName: string } | null>(null);
  const [selectedMember, setSelectedMember] = useState<SiteMember | null>(null);
  const [showMemberMenu, setShowMemberMenu] = useState(false);
  const [profileUserId, setProfileUserId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadSiteMembers();
    }
  }, [isOpen, siteId]);

  const loadSiteMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      const membersData = await sitesV2Api.getSiteMembers(siteId);
      setSiteMembers(membersData);
    } catch (err) {
      console.error('Error loading site members:', err);
      setError('Ошибка загрузки участников объекта');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (userId: number) => {
    try {
      setAddingMember(userId);
      await questionsV2Api.addQuestionMember(siteId, questionId, userId);
      onMembersUpdated();
    } catch (err: any) {
      console.error('Error adding member:', err);
      setError('Ошибка добавления участника');
    } finally {
      setAddingMember(null);
    }
  };

  const handleRemoveMember = async (userId: number) => {
    try {
      await questionsV2Api.removeQuestionMember(siteId, questionId, userId);
      onMembersUpdated();
    } catch (err: any) {
      console.error('Error removing member:', err);
      setError('Ошибка исключения участника');
    }
  };

  const isMember = (userId: number) => members.some(p => p.userId === userId);
  const isCreator = (userId: number) => userId === user?.id;

  const getUserInitials = (name: string): string =>
    name.split(' ').map(w => w.charAt(0)).join('').toUpperCase().slice(0, 2);

  const getAvatarColor = (userId: number): string => {
    const colors = [
      '#ef4444', '#f97316', '#f59e0b', '#eab308',
      '#84cc16', '#22c55e', '#10b981', '#06b6d4',
      '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
      '#a855f7', '#d946ef', '#ec4899', '#f43f5e'
    ];
    const index = userId.toString().split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  const getMembersTitle = () => {
    switch (typeId) {
      case 1: return 'Участники';
      case 2: return 'Наблюдатели';
      case 3: return 'Участники';
      default: return 'Участники';
    }
  };

  const openMemberMenu = (member: SiteMember) => {
    setSelectedMember(member);
    setShowMemberMenu(true);
  };

  const closeMemberMenu = () => {
    setShowMemberMenu(false);
    setSelectedMember(null);
  };

  const closeProfileSheet = () => {
    setProfileUserId(null);
  };

  const handleMenuRemove = () => {
    if (selectedMember) handleRemoveMember(selectedMember.userId);
    closeMemberMenu();
  };
  const handleMenuPermissions = () => {
    if (selectedMember) setPermissionsMember({ userId: selectedMember.userId, userName: selectedMember.userName || 'Участник' });
    closeMemberMenu();
  };
  const handleMenuProfile = () => {
    if (selectedMember) {
      setProfileUserId(selectedMember.userId);
      setShowMemberMenu(false);
      setSelectedMember(null);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <BottomSheet isOpen={isOpen} title={getMembersTitle()} onClose={onClose} size="auto">
        <div className="question-members-modal">
          {error && (
            <div className="error-message">
              <i className="ri-error-warning-line"></i>
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="loading-state">
              <i className="ri-loader-4-line ri-spin"></i>
              <span>Загрузка участников...</span>
            </div>
          ) : (
            <div className="members-content">
              {/* Участники чата */}
              <div className="members-section">
                <div className="members-list">
                  {siteMembers
                    .filter(m => isMember(m.userId) || isCreator(m.userId))
                    .map(member => {
                      const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '');
                      const avatarUrl = member.avatarUrl
                        ? (member.avatarUrl.startsWith('http') ? member.avatarUrl : `${apiBaseUrl}${member.avatarUrl}`)
                        : null;

                      return (
                        <div
                          key={member.userId}
                          className="member-item"
                          onClick={() => openMemberMenu(member)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && openMemberMenu(member)}
                        >
                          <div className="member-avatar">
                            {avatarUrl && (
                              <img
                                src={avatarUrl}
                                alt={member.userName || 'Аватар'}
                                className="avatar-image"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                                onLoad={(e) => {
                                  const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                                  if (fallback) fallback.style.display = 'none';
                                }}
                              />
                            )}
                            <div
                              className="avatar-circle"
                              style={{ background: getAvatarColor(member.userId), display: avatarUrl ? 'none' : 'flex' }}
                            >
                              <span>{getUserInitials(member.userName || 'U')}</span>
                            </div>
                          </div>
                          <div className="member-info">
                            <div className="member-name">
                              {member.userName || 'Неизвестный пользователь'}
                              {isCreator(member.userId) && <span className="creator-badge">Создатель</span>}
                            </div>
                            <RoleBadges roles={member.roles} fallbackRoleName={member.roleName || 'Роль не назначена'} />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Добавить участника */}
              <div className="available-section">
                <h3 className="section-title">
                  <i className="ri-user-add-line"></i>
                  Добавить участника
                </h3>
                <div className="available-list">
                  {siteMembers
                    .filter(m => !isMember(m.userId) && !isCreator(m.userId))
                    .map(member => {
                      const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '');
                      const avatarUrl = member.avatarUrl
                        ? (member.avatarUrl.startsWith('http') ? member.avatarUrl : `${apiBaseUrl}${member.avatarUrl}`)
                        : null;

                      return (
                        <div
                          key={member.userId}
                          className="available-item"
                          onClick={() => handleAddMember(member.userId)}
                        >
                          <div className="member-avatar">
                            {avatarUrl && (
                              <img
                                src={avatarUrl}
                                alt={member.userName || 'Аватар'}
                                className="avatar-image"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                                onLoad={(e) => {
                                  const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                                  if (fallback) fallback.style.display = 'none';
                                }}
                              />
                            )}
                            <div
                              className="avatar-circle"
                              style={{ background: getAvatarColor(member.userId), display: avatarUrl ? 'none' : 'flex' }}
                            >
                              <span>{getUserInitials(member.userName || 'U')}</span>
                            </div>
                          </div>
                          <div className="member-info">
                            <div className="member-name">{member.userName || 'Неизвестный пользователь'}</div>
                            <div className="member-role">{member.roleName || 'Роль не назначена'}</div>
                          </div>
                          {addingMember === member.userId && (
                            <i className="ri-loader-4-line ri-spin adding-spinner"></i>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>

              {siteMembers.filter(m => !isMember(m.userId) && !isCreator(m.userId)).length === 0 && (
                <div className="no-available-message">
                  <i className="ri-user-line"></i>
                  <span>Все участники объекта уже добавлены</span>
                </div>
              )}
            </div>
          )}
        </div>
      </BottomSheet>

      {/* Меню участника (по нажатию) */}
      <BottomSheet
        isOpen={showMemberMenu}
        onClose={closeMemberMenu}
        title={selectedMember?.userName || 'Действия'}
        size="small"
      >
        <ButtonList
          spacing="normal"
          showDividers={true}
          items={[
            {
              id: 'remove',
              icon: 'ri-user-unfollow-line',
              label: 'Исключить',
              disabled: selectedMember ? isCreator(selectedMember.userId) : true,
              onClick: handleMenuRemove
            },
            {
              id: 'permissions',
              icon: 'ri-shield-user-line',
              label: 'Права',
              onClick: handleMenuPermissions
            },
            {
              id: 'profile',
              icon: 'ri-user-line',
              label: 'Профиль',
              onClick: handleMenuProfile
            }
          ]}
        />
      </BottomSheet>

      {permissionsMember && (
        <ChatMemberPermissionsModal
          isOpen={!!permissionsMember}
          siteId={siteId}
          questionId={questionId}
          userId={permissionsMember.userId}
          typeId={typeId}
          userName={permissionsMember.userName}
          onClose={() => setPermissionsMember(null)}
          onSaved={() => onMembersUpdated()}
        />
      )}

      {/* Боттомшит профиля — elevated, чтобы всегда поверх модалки участников */}
      <BottomSheet
        isOpen={profileUserId !== null}
        onClose={closeProfileSheet}
        size="fullscreen"
        elevated
      >
        <div className="question-members-profile-sheet">
          {profileUserId != null && (
            <ProfilePage
              userId={profileUserId}
              hideHeader
              onBack={closeProfileSheet}
            />
          )}
        </div>
      </BottomSheet>
    </>
  );
};

export default QuestionMembersModal;
